import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths, isToday } from 'date-fns';
import { formatDateForDisplay, getFutureISO, getThisWeekendISO, getNextMondayISO } from '../utils/dateUtils';
import { Button, ActionIcon } from '@mantine/core';
import './CustomDatePicker.css';

interface CustomDatePickerProps {
  value?: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  displayFormat?: 'standard' | 'inline'; // Add display format option
  showIcon?: boolean; // Control icon display
  showSnooze?: boolean; // Control snooze display
  renderTrigger?: (onClick: () => void) => React.ReactNode; // Custom trigger renderer
}

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  className = '',
  placeholder = 'Select date',
  displayFormat = 'standard',
  showIcon = true,
  showSnooze = false,
  renderTrigger
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(value ? new Date(value) : new Date());
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const selectedDate = value ? new Date(value) : null;

  // Calculate optimal position for dropdown
  const calculatePosition = useCallback(() => {
    // Always use wrapper ref since we removed the input element
    const targetElement = wrapperRef.current;
    if (!targetElement) return;
    
    const inputRect = targetElement.getBoundingClientRect();
    const dropdownHeight = showSnooze ? 450 : 350; // Adjust height based on snooze section
    const dropdownWidth = 280;
    const padding = 8;
    const isMobile = window.innerWidth <= 768;
    
    let top = inputRect.bottom + padding;
    let left = inputRect.left;
    
    // Mobile: center vertically on screen for better visibility
    if (isMobile) {
      left = 20;
      
      // Center the dropdown vertically on mobile screens
      const viewportHeight = window.innerHeight;
      const actualDropdownHeight = Math.min(dropdownHeight, viewportHeight - 40); // 40px total padding
      top = (viewportHeight - actualDropdownHeight) / 2;
      
      // Set the constrained height if needed
      if (dropdownHeight > viewportHeight - 40) {
        document.documentElement.style.setProperty('--dropdown-max-height', `${actualDropdownHeight}px`);
      } else {
        // Clear any previous height constraint
        document.documentElement.style.removeProperty('--dropdown-max-height');
      }
    } else {
      // Desktop positioning logic
      // Adjust if dropdown would go outside right edge
      if (left + dropdownWidth > window.innerWidth) {
        left = window.innerWidth - dropdownWidth - padding;
      }
      
      // Ensure it doesn't go outside left edge
      if (left < padding) {
        left = padding;
      }
      
      // Adjust if dropdown would go below viewport
      if (top + dropdownHeight > window.innerHeight) {
        top = inputRect.top - dropdownHeight - padding;
        // If still outside viewport, position at top of screen
        if (top < padding) {
          top = padding;
        }
      }
    }
    
    setPosition({ top, left });
  }, [displayFormat, showSnooze]);

  // Handle repositioning on resize/scroll
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        calculatePosition();
      }
    };

    if (isOpen) {
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleResize);
      };
    }
  }, [isOpen, calculatePosition]);

  const handleInputClick = () => {
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  const handleDateSelect = (date: Date) => {
    // Use local date to avoid timezone issues that cause off-by-one errors
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const localDateString = `${year}-${month}-${day}`;
    onChange(localDateString);
    setIsOpen(false);
  };

  const handlePrevMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentMonth(prev => subMonths(prev, 1));
  };

  const handleNextMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentMonth(prev => addMonths(prev, 1));
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
  };

  const handleToday = (e: React.MouseEvent) => {
    e.stopPropagation();
    const today = new Date();
    // Use local date components to avoid timezone issues that cause off-by-one errors
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const localDateString = `${year}-${month}-${day}`;
    onChange(localDateString);
    setIsOpen(false);
  };

  const handleSnooze = (newDate: string | undefined) => {
    if (newDate) {
      onChange(newDate);
      setIsOpen(false);
    } else {
      onChange('');
      setIsOpen(false);
    }
  };

  // Snooze action generators using utility functions
  const getRandomFutureDate = (): string => {
    const minDays = 8; // At least 1 week + 1 day
    const maxDays = 30; // No more than 1 month
    const randomDays = Math.floor(Math.random() * (maxDays - minDays + 1)) + minDays;
    return getFutureISO(randomDays);
  };

  const getSnoozeOptions = () => {
    const currentDate = value ? new Date(value) : null;
    
    const allOptions = [
      { label: 'Today', date: getFutureISO(0), icon: '📅', days: 0 },
      { label: 'Tomorrow', date: getFutureISO(1), icon: '🌅', days: 1 },
      { label: '3 days', date: getFutureISO(3), icon: '📆', days: 3 },
      { label: 'This Weekend', date: getThisWeekendISO(), icon: '🏖️', days: -2 }, // Special flag for weekend
      { label: '1 week', date: getFutureISO(7), icon: '🗓️', days: 7 },
      { label: 'Next Monday', date: getNextMondayISO(), icon: '💼', days: -3 }, // Special flag for next Monday
      { label: 'Later', date: getRandomFutureDate(), icon: '⏰', days: -1 } // -1 means always show
    ];

    // Filter out options that match the current due date
    const filteredOptions = allOptions.filter(option => {
      if (option.days === -1) return true; // Always show "Later"
      if (!currentDate) return true; // Show all if no current date
      
      // For special date calculations (weekend, next Monday)
      if (option.days < 0 && option.days !== -1) {
        const optionDate = new Date(option.date);
        const currentDateString = currentDate.toDateString();
        const optionDateString = optionDate.toDateString();
        return currentDateString !== optionDateString;
      }
      
      // For regular day-based calculations
      const optionDate = new Date();
      optionDate.setDate(optionDate.getDate() + option.days);
      
      // Compare dates (ignore time)
      const currentDateString = currentDate.toDateString();
      const optionDateString = optionDate.toDateString();
      
      return currentDateString !== optionDateString;
    });

    // Sort by actual date, keeping "Later" at the end
    return filteredOptions.sort((a, b) => {
      // Always keep "Later" at the end
      if (a.days === -1) return 1;
      if (b.days === -1) return -1;
      
      // Sort by actual date
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });
  };

  // Get all days for the current month view
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Add padding days to start from Monday
  const startDay = monthStart.getDay();
  const paddingStart = startDay === 0 ? 6 : startDay - 1; // Convert Sunday (0) to 6, Monday (1) to 0, etc.
  
  const paddingDays = [];
  for (let i = paddingStart; i > 0; i--) {
    const paddingDate = new Date(monthStart);
    paddingDate.setDate(paddingDate.getDate() - i);
    paddingDays.push(paddingDate);
  }

  const allDays = [...paddingDays, ...calendarDays];

  const formatDisplayValue = () => {
    if (!selectedDate) return '';
    if (displayFormat === 'inline') {
      // Use the same format as the original inline date picker
      return formatDateForDisplay(selectedDate.toISOString());
    }
    return format(selectedDate, 'dd/MM/yyyy');
  };

  return (
    <div className={`custom-date-picker ${className}`} ref={dropdownRef}>
      {renderTrigger ? (
        // Custom trigger
        <div ref={wrapperRef}>
          {renderTrigger(handleInputClick)}
        </div>
      ) : (
        // Default trigger
        <div ref={wrapperRef}>
          {displayFormat === 'inline' ? (
            <Button
              variant="subtle"
              size="xs"
              leftSection={showIcon ? <Calendar size={12} /> : undefined}
              onClick={handleInputClick}
              style={{ 
                padding: '4px 8px', 
                height: '24px',
                minHeight: '24px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {formatDisplayValue() || placeholder}
            </Button>
          ) : (
            <Button
              variant="outline"
              leftSection={showIcon ? <Calendar size={16} /> : undefined}
              onClick={handleInputClick}
              fullWidth
              justify="flex-start"
              size="sm"
            >
              {formatDisplayValue() || placeholder}
            </Button>
          )}
        </div>
      )}

      {isOpen && createPortal(
        <>
          {/* Invisible overlay to capture outside clicks */}
          <div 
            className="date-picker-overlay"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(false);
            }}
          />
          <div 
            ref={dropdownRef}
            className={`date-picker-dropdown ${window.innerWidth <= 768 ? 'mobile-constrained' : ''}`}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              maxHeight: window.innerWidth <= 768 ? 'var(--dropdown-max-height, calc(100vh - 40px))' : undefined
            }}
          >
          <div className="date-picker-header">
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={handlePrevMonth}
            >
              <ChevronLeft size={16} />
            </ActionIcon>
            <h3 className="month-year-title">
              {format(currentMonth, 'MMMM yyyy')}
            </h3>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={handleNextMonth}
            >
              <ChevronRight size={16} />
            </ActionIcon>
          </div>

          <div className="date-picker-calendar">
            <div className="weekday-headers">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className="weekday-header">
                  {day}
                </div>
              ))}
            </div>

            <div className="calendar-grid">
              {allDays.map((date, index) => {
                const isSelected = selectedDate && isSameDay(date, selectedDate);
                const isCurrentMonth = isSameMonth(date, currentMonth);
                const isTodayDate = isToday(date);

                return (
                  <button
                    key={index}
                    type="button"
                    className={`calendar-day ${isSelected ? 'selected' : ''} ${
                      !isCurrentMonth ? 'other-month' : ''
                    } ${isTodayDate ? 'today' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDateSelect(date);
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {format(date, 'd')}
                  </button>
                );
              })}
            </div>
          </div>

          {showSnooze && (
            <div className="date-picker-snooze-section">
              <div className="snooze-section-header">
                <span>Quick Actions</span>
              </div>
              <div className="snooze-section-content">
                <div className="snooze-actions-grid">
                  {getSnoozeOptions().map((option, index) => {
                    // Format the date for the tooltip
                    const tooltipDate = new Date(option.date);
                    const tooltipText = `${option.label} (${format(tooltipDate, 'EEEE, MMMM d, yyyy')})`;
                    
                    return (
                      <button
                        key={index}
                        type="button"
                        className="quick-action-button"
                        onClick={() => handleSnooze(option.date)}
                        title={tooltipText}
                      >
                        <span className="action-icon">{option.icon}</span>
                        <span className="action-label">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="date-picker-footer">
            <Button
              variant="subtle"
              size="xs"
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button
              variant="filled"
              size="xs"
              onClick={handleToday}
            >
              Today
            </Button>
          </div>
        </div>
        </>,
        document.body
      )}
    </div>
  );
};
